import type { FormField } from '../models/form';


/**
 * Resolves the effective multi-file mode.
 *
 * Runtime 2.3.0 accidentally persisted new upload controls with
 * fileMultiple=false while also setting maximumFiles=5. Treat that
 * contradictory shape as multi-file so existing 2.3 forms are repaired
 * without a database migration. An explicit single-file choice is stored
 * with maximumFiles=1 by the property editor.
 */
export function isMultipleFileField(field: Pick<FormField, 'fileMultiple' | 'validation'>): boolean {
  const maximum = Number(field.validation?.maximumFiles ?? 1);
  return field.fileMultiple === true || (Number.isFinite(maximum) && maximum > 1);
}

export function parseFileValues(value: unknown, multiple = false): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item ?? '').trim()).filter(Boolean);
  const text = String(value ?? '').trim();
  if (!text) return [];
  if (!multiple) return [text];
  try {
    const parsed = JSON.parse(text) as unknown;
    if (Array.isArray(parsed)) return parsed.map((item) => String(item ?? '').trim()).filter(Boolean);
  } catch { /* Legacy single-file value inside a newly multi-file field. */ }
  return [text];
}

export function serializeFileValues(files: string[], multiple = false): string {
  const clean = files.map((item) => item.trim()).filter(Boolean);
  if (!multiple) return clean[0] ?? '';
  return clean.length ? JSON.stringify(clean) : '';
}
